import { useState } from 'react'

import { KTIcon } from '../../../../../_metronic/helpers'
import { EMatchingStatus } from '../../../../../types/enums'
import { MatchingPayoutTicket } from '../../core/_models'
import { ActivePayoutTicketRowMenu } from '../payouts-active/ActivePayoutTicketRowMenu'
import { PayoutPauseButton } from './PayoutPauseButton'
import { ReplacePayoutRequisiteModal } from './ReplacePayoutRequisiteModal'
import { VirtualPaymentState } from './usePayoutTicketActions'

interface Props {
  ticket: MatchingPayoutTicket
  canManageTickets: boolean
  canCreateVirtualPayment: boolean
  canResend: boolean
  /** Виртуальный платёж по выплате: идёт запрос — кнопка погашена, создан — скрыта до перечитывания. */
  virtualPaymentState?: VirtualPaymentState
  /** Второй аргумент — выплата на паузе: окно подтверждения предупреждает об этом. */
  onPartialComplete?: (ticketToken: string, paused?: boolean) => void
  onCancel?: (ticketToken: string, paused?: boolean) => void
  /** Состояние выплаты изменилось — реквизит или пауза: экран перечитывается. */
  onRequisiteReplaced?: () => void
}

/**
 * Действия над матчинговой выплатой: виртуальный платёж, пауза, отмена, смена реквизита,
 * переотправка КБ.
 *
 * Один компонент на два места: строка активной выплаты и карточка выплаты.
 */
export const PayoutRowActions = ({
  ticket,
  canManageTickets,
  canCreateVirtualPayment,
  canResend,
  virtualPaymentState = 'idle',
  onPartialComplete,
  onCancel,
  onRequisiteReplaced,
}: Props) => {
  const [replacingRequisite, setReplacingRequisite] = useState<boolean>(false)

  // Гейт обязан совпадать с бэкендом: там условие — matching_ticket.status = PROCESSING, а НЕ
  // статус выплаты. Список активных запрашивается только со статусом MATCHING_PROCESSING, поэтому
  // ticket.status здесь всегда один и тот же и ничего не отсекает, а HALTED-тикет на странице есть
  // и получил бы отказ уже после заполнения формы.
  // Плюс нужны адресуемые каналы: у выплаты без строк связки рисуется fallback из копии в
  // matching_ticket, менять там нечего. Роли — те же, что у остальных действий строки.
  const canReplaceRequisite =
    ticket.matchingStatus === EMatchingStatus.PROCESSING && (ticket.requisites?.length ?? 0) > 0

  // Пауза — признак мэтчинг-тикета в работе. Ответ, который его не отдаёт, кнопку не получает:
  // рисовать «Приостановить» у выплаты, которая уже на паузе, хуже, чем не рисовать ничего.
  const paused = ticket.matchingPaused === true
  const canPause =
    ticket.matchingStatus === EMatchingStatus.PROCESSING && typeof ticket.matchingPaused === 'boolean'

  const handlePartialComplete = () => {
    if (!canManageTickets) return
    onPartialComplete?.(ticket.ticketToken, paused)
  }

  const handleCancel = () => {
    if (!canManageTickets) return
    onCancel?.(ticket.ticketToken, paused)
  }

  return (
    <>
      <div className='d-flex justify-content-end gap-2'>
        {canManageTickets && (
          <>
            {canCreateVirtualPayment &&
              ticket.matchingRemainder > 0 &&
              virtualPaymentState !== 'created' && (
                <button
                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                  onClick={handlePartialComplete}
                  disabled={virtualPaymentState === 'submitting'}
                  title={
                    virtualPaymentState === 'submitting'
                      ? 'Создаём виртуальный платёж…'
                      : 'Создать виртуальный платеж'
                  }
                >
                  {virtualPaymentState === 'submitting' ? (
                    <span className='spinner-border spinner-border-sm text-primary' />
                  ) : (
                    <KTIcon className='fs-1 text-primary' iconName='check-circle' />
                  )}
                </button>
              )}

            {canPause && (
              <PayoutPauseButton
                token={ticket.ticketToken}
                paused={paused}
                onChanged={() => onRequisiteReplaced?.()}
              />
            )}

            <button
              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
              onClick={handleCancel}
              title='Отменить тикет без пересоздания'
            >
              <KTIcon className='fs-1 text-danger' iconName='cross-circle' />
            </button>
          </>
        )}

        <ActivePayoutTicketRowMenu
          ticketToken={ticket.ticketToken}
          canResend={canResend}
          canReplaceRequisite={canManageTickets && canReplaceRequisite}
          onReplaceRequisite={() => setReplacingRequisite(true)}
        />
      </div>
      {replacingRequisite && (
        <ReplacePayoutRequisiteModal
          show={replacingRequisite}
          payoutToken={ticket.ticketToken}
          requisites={ticket.requisites ?? []}
          onClose={() => setReplacingRequisite(false)}
          onReplaced={() => {
            setReplacingRequisite(false)
            onRequisiteReplaced?.()
          }}
        />
      )}
    </>
  )
}

export default PayoutRowActions
